import { SPOTIFY_CONFIG } from '../config/spotify';
import { SPOTIFY_FALLBACK } from '../data/spotifyFallback';
import { firebaseCacheService, SpotifyTrack, SpotifyArtist } from './firebaseCache';

interface ImportMetaEnv {
  VITE_SPOTIFY_ARTIST_ID: string;
  PROD: boolean;
  DEV: boolean;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';
const ARTIST_ID = import.meta.env.VITE_SPOTIFY_ARTIST_ID;

// Use imported fallback data
const STATIC_FALLBACK = SPOTIFY_FALLBACK;

class SpotifyWithFirebaseService {
  private accessToken: string = '';
  private tokenExpirationTime: number = 0;
  private maxRetries: number = 5;
  private baseRetryDelay: number = 1000;
  private lastRequestTime: number = 0;
  private minRequestInterval: number = 200;
  private maxConcurrentRequests: number = 10;
  private backgroundSyncInProgress = false;

  /**
   * Get artist data - tries Firebase first, then Spotify API
   */
  async getArtist(): Promise<SpotifyArtist | null> {
    if (!ARTIST_ID) {
      throw new Error('No artist ID configured');
    }

    try {
      // Try Firebase cache first
      const cachedData = await firebaseCacheService.getCachedData();
      if (cachedData?.artist) {
        // Trigger background refresh if needed
        this.triggerBackgroundSync();
        
        return cachedData.artist;
      }

      // Fallback to Spotify API
      await this.ensureValidToken();
      const response = await fetch(`${SPOTIFY_API_BASE}/artists/${ARTIST_ID}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch artist');
      }

      const artist = await response.json();
      
      // Save to Firebase
      await firebaseCacheService.updateCache({ artist });
      
      return artist;
    } catch (error) {
      console.error('Error fetching artist:', error);
      return STATIC_FALLBACK.artist;
    }
  }

  /**
   * Get top tracks - tries Firebase first, then Spotify API
   */
  async getArtistTopTracks(market: string = 'US'): Promise<SpotifyTrack[]> {
    if (!ARTIST_ID) {
      throw new Error('No artist ID configured');
    }

    try {
      // Try Firebase cache first
      const cachedData = await firebaseCacheService.getCachedData();
      if (cachedData?.topTracks && cachedData.topTracks.length > 0) {
        // Trigger background refresh if needed
        this.triggerBackgroundSync();
        
        return cachedData.topTracks;
      }

      // Fallback to Spotify API
      await this.ensureValidToken();
      const response = await fetch(
        `${SPOTIFY_API_BASE}/artists/${ARTIST_ID}/top-tracks?market=${encodeURIComponent(market)}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch top tracks');
      }

      const data = await response.json();
      const topTracks: SpotifyTrack[] = data.tracks.map((t: any) => ({
        id: t.id,
        name: t.name,
        album: {
          images: t.album?.images || [],
          name: t.album?.name || 'Unknown',
          release_date: t.album?.release_date || '1970-01-01'
        },
        duration_ms: t.duration_ms,
        preview_url: t.preview_url ?? null
      }));

      // Save to Firebase
      await firebaseCacheService.updateCache({ topTracks });

      return topTracks;
    } catch (error) {
      console.error('Error fetching top tracks:', error);
      return [];
    }
  }

  /**
   * Get all artist tracks - tries Firebase first for instant load
   */
  async getArtistTracks(
    initialLoadOnly: boolean = false,
    progressCallback?: (tracks: SpotifyTrack[], progress: { current: number, total: number }) => void
  ): Promise<SpotifyTrack[]> {
    if (!ARTIST_ID) {
      throw new Error('No artist ID configured');
    }

    try {
      // ALWAYS try Firebase cache first for instant load
      const cachedData = await firebaseCacheService.getCachedData();
      
      if (cachedData?.tracks && cachedData.tracks.length > 0) {
        // Trigger background refresh if cache is getting old
        this.triggerBackgroundSync();
        
        return cachedData.tracks;
      }

      // If no cache and this is initial load only, return empty
      if (initialLoadOnly) {
        return [];
      }

      // No cache exists, fetch from Spotify API
      const tracks = await this.fetchAllTracksFromSpotify(progressCallback);
      
      // Save to Firebase for future instant loads
      if (tracks.length > 0) {
        await firebaseCacheService.setCachedData({ tracks });
      }
      
      return tracks;
    } catch (error) {
      console.error('Error in getArtistTracks:', error);
      return STATIC_FALLBACK.tracks;
    }
  }

  /**
   * Trigger a background sync if cache needs refresh
   */
  private async triggerBackgroundSync(): Promise<void> {
    // Don't start multiple syncs
    if (this.backgroundSyncInProgress || firebaseCacheService.isSyncInProgress()) {
      return;
    }

    try {
      const needsRefresh = await firebaseCacheService.needsRefresh();
      
      if (!needsRefresh) {
        return;
      }

      this.backgroundSyncInProgress = true;
      firebaseCacheService.setSyncing(true);

      // Run sync in background without blocking
      this.performBackgroundSync().catch(error => {
        console.error('Background sync failed:', error);
      }).finally(() => {
        this.backgroundSyncInProgress = false;
        firebaseCacheService.setSyncing(false);
      });
    } catch (error) {
      console.error('Error checking if sync needed:', error);
    }
  }

  /**
   * Perform the actual background sync
   */
  private async performBackgroundSync(): Promise<void> {
    try {
      // Fetch fresh data from Spotify
      const [artist, topTracks, allTracks] = await Promise.all([
        this.fetchArtistFromSpotify(),
        this.fetchTopTracksFromSpotify(),
        this.fetchAllTracksFromSpotify()
      ]);

      // Update Firebase cache with fresh data
      await firebaseCacheService.setCachedData({
        artist,
        topTracks,
        tracks: allTracks
      });
    } catch (error) {
      console.error('Background sync error:', error);
      throw error;
    }
  }

  /**
   * Fetch artist data directly from Spotify API
   */
  private async fetchArtistFromSpotify(): Promise<SpotifyArtist> {
    await this.ensureValidToken();
    const response = await fetch(`${SPOTIFY_API_BASE}/artists/${ARTIST_ID}`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch artist from Spotify');
    }

    return await response.json();
  }

  /**
   * Fetch top tracks directly from Spotify API
   */
  private async fetchTopTracksFromSpotify(market: string = 'US'): Promise<SpotifyTrack[]> {
    await this.ensureValidToken();
    const response = await fetch(
      `${SPOTIFY_API_BASE}/artists/${ARTIST_ID}/top-tracks?market=${encodeURIComponent(market)}`,
      {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch top tracks from Spotify');
    }

    const data = await response.json();
    return data.tracks.map((t: any) => ({
      id: t.id,
      name: t.name,
      album: {
        images: t.album?.images || [],
        name: t.album?.name || 'Unknown',
        release_date: t.album?.release_date || '1970-01-01'
      },
      duration_ms: t.duration_ms,
      preview_url: t.preview_url ?? null
    }));
  }

  /**
   * Fetch all tracks from Spotify API (the slow part)
   */
  private async fetchAllTracksFromSpotify(
    progressCallback?: (tracks: SpotifyTrack[], progress: { current: number, total: number }) => void
  ): Promise<SpotifyTrack[]> {
    await this.ensureValidToken();

    // Get all albums
    let allAlbums: any[] = [];
    let offset = 0;
    const limit = 50;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(
        `${SPOTIFY_API_BASE}/artists/${ARTIST_ID}/albums?include_groups=album,single&limit=${limit}&offset=${offset}&market=US`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch albums');
      }

      const albumsData = await response.json();
      allAlbums.push(...albumsData.items);
      hasMore = albumsData.items.length === limit && albumsData.next;
      offset += limit;

      if (hasMore) {
        await this.wait(this.minRequestInterval);
      }
    }

    // Fetch tracks from albums in batches
    const allTracks: SpotifyTrack[] = [];
    const albumBatchSize = 10;

    for (let batchStart = 0; batchStart < allAlbums.length; batchStart += albumBatchSize) {
      const batchEnd = Math.min(batchStart + albumBatchSize, allAlbums.length);
      const albumBatch = allAlbums.slice(batchStart, batchEnd);

      const batchPromises = albumBatch.map(async (album) => {
        try {
          await this.ensureValidToken();
          const response = await fetch(
            `${SPOTIFY_API_BASE}/albums/${album.id}/tracks?limit=50&market=US`,
            {
              headers: {
                'Authorization': `Bearer ${this.accessToken}`
              }
            }
          );

          if (!response.ok) {
            return [];
          }

          const tracksData = await response.json();
          return tracksData.items.map((track: any) => ({
            id: track.id,
            name: track.name,
            album: {
              images: album.images,
              name: album.name,
              release_date: album.release_date
            },
            duration_ms: track.duration_ms,
            preview_url: track.preview_url
          }));
        } catch (error) {
          return [];
        }
      });

      const batchResults = await Promise.all(batchPromises);
      const batchTracks = batchResults.flat();
      allTracks.push(...batchTracks);

      // Call progress callback
      if (progressCallback && allTracks.length > 0) {
        progressCallback(allTracks, {
          current: batchEnd,
          total: allAlbums.length
        });
      }

      // Add delay between batches
      if (batchEnd < allAlbums.length) {
        await this.wait(500);
      }
    }

    // Remove duplicates and sort by release date
    const uniqueTracks = Array.from(
      new Map(allTracks.map(track => [track.id, track])).values()
    );
    uniqueTracks.sort((a, b) => 
      new Date(b.album.release_date).getTime() - new Date(a.album.release_date).getTime()
    );

    return uniqueTracks;
  }

  /**
   * Authenticate with Spotify
   */
  private async authenticate(): Promise<void> {
    try {
      if (!SPOTIFY_CONFIG.clientId || !SPOTIFY_CONFIG.clientSecret) {
        throw new Error('Missing Spotify credentials');
      }

      const params = new URLSearchParams();
      params.append('grant_type', 'client_credentials');

      const auth = btoa(`${SPOTIFY_CONFIG.clientId}:${SPOTIFY_CONFIG.clientSecret}`);

      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      this.accessToken = data.access_token;
      this.tokenExpirationTime = Date.now() + (data.expires_in * 1000);
    } catch (error) {
      console.error('Authentication error:', error);
      throw error;
    }
  }

  /**
   * Ensure we have a valid access token
   */
  private async ensureValidToken(): Promise<void> {
    if (!this.accessToken || Date.now() >= this.tokenExpirationTime) {
      await this.authenticate();
    }
  }

  /**
   * Wait helper
   */
  private async wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear all caches (both Firebase and local)
   */
  async clearAllCaches(): Promise<void> {
    await firebaseCacheService.clearCache();
  }

  /**
   * Force a refresh from Spotify API
   */
  async forceRefresh(): Promise<void> {
    this.backgroundSyncInProgress = true;
    firebaseCacheService.setSyncing(true);

    try {
      await this.performBackgroundSync();
    } finally {
      this.backgroundSyncInProgress = false;
      firebaseCacheService.setSyncing(false);
    }
  }
}

export const spotifyWithFirebaseService = new SpotifyWithFirebaseService();

